import { useMemo } from 'react'

import { type InAppNotificationResponse, useListInAppNotificationsQuery } from '~/data/api/client/workflows'
import { usePolling } from '~/data/api/hooks/polling'
import type { Priority } from '~/data/types/notification'

/**
 * One row in the notifications surface, normalized from a Workflows in-app
 * notification.
 */
export type DisplayNotification = {
  id: string
  title: string
  body: string
  priority: Priority
  /** ISO timestamp of the notification's `createdAt`. */
  createdAt: string
  /** Drives unread styling and is set when the user opens the notification
   *  (see `useMarkNotificationRead`). */
  read: boolean
  source?: string
  workflowRunId?: string
  /** The body with entity references as links; rendered by the detail overlay. */
  bodySegments?: InAppNotificationResponse['bodySegments'] | null
}

type Scope = 'bell' | 'all'

type UseMergedNotificationsArgs = {
  /**
   * Names the calling surface. Both scopes behave identically here: each lists
   * the newest notifications regardless of read state, so a row stays visible
   * after the user reads it, and each takes `unreadCount` from the count-only
   * query's server-side total — a count derived from the fetched page
   * undercounts once unread rows fall outside it. Callers size their own list
   * through `count`.
   */
  scope?: Scope
  /**
   * Applies the read filter on the row query itself, so the unread view spans
   * all unread notifications server-side — filtering the fetched page
   * client-side would drop unread rows older than the newest `count` read ones.
   */
  unreadOnly?: boolean
  count?: number
  intervalSeconds?: number
  /**
   * Keep polling while the window is unfocused. The desktop shell's tray/badge
   * feed needs this — a menu-bar health indicator that only updates while the
   * window is focused defeats its purpose.
   */
  ignoreFocus?: boolean
  /**
   * Skip the underlying RTK queries. Used by callers that only mount this feed
   * behind a feature gate (e.g. desktop tray) so gate-off sessions do not poll
   * the rollout-limited workflows in-app notifications API.
   */
  skip?: boolean
}

export type MergedNotifications = {
  rows: DisplayNotification[]
  unreadCount: number
  isLoading: boolean
  /** True when a poll failed and no cached data is available to show. */
  hasFetchError: boolean
}

export function useMergedNotifications({
  unreadOnly = false,
  count = 50,
  intervalSeconds = 60,
  ignoreFocus = false,
  skip = false
}: UseMergedNotificationsArgs = {}): MergedNotifications {
  const workflow = usePolling(
    useListInAppNotificationsQuery,
    {
      ...(unreadOnly && { read: false }),
      page: 0,
      count
    },
    { intervalSeconds, ignoreFocus, skip }
  )

  // A count-only query (a single row) whose pagination metadata carries the
  // server-side unread total behind the bell badge and the bulk action.
  const workflowUnread = usePolling(
    useListInAppNotificationsQuery,
    { read: false, page: 0, count: 1 },
    { intervalSeconds, ignoreFocus, skip }
  )

  // `currentData` (not `data`) so toggling the read filter can't keep serving
  // the previous filter's page while the refetch is in flight.
  const rows = useMemo<DisplayNotification[]>(
    () =>
      (workflow.currentData?.data ?? []).map((n) => ({
        id: n.id,
        title: n.title,
        body: n.body,
        priority: n.priority,
        createdAt: n.createdAt,
        read: n.read,
        source: n.source,
        workflowRunId: n.workflowRunId,
        bodySegments: n.bodySegments
      })),
    [workflow.currentData]
  )

  const unreadFromRows = rows.reduce((sum, row) => (row.read ? sum : sum + 1), 0)
  // Until the count query lands, fall back to the fetched page's unread rows
  // so the bell badge doesn't blink out on first open.
  const unreadCount = workflowUnread.data?.meta.pagination.totalItems ?? unreadFromRows

  return {
    rows,
    unreadCount,
    // Loading only on first load and a filter toggle's refetch; a settled
    // error (isFetching false) falls through to the empty state.
    isLoading: workflow.currentData === undefined && workflow.isFetching,
    hasFetchError: workflow.isError && workflow.currentData === undefined
  }
}
